import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INITIATIVE_OPTIONS = [
  { v: "regular", l: "Regular" },
  { v: "deposit", l: "Deposit" },
  { v: "referral", l: "Referral" },
  { v: "tebus_murah", l: "Tebus Murah" },
  { v: "lifetime", l: "Lifetime Package" },
  { v: "exclusive_member", l: "Exclusive Member" },
  { v: "membership", l: "Membership" },
  { v: "birthday", l: "Birthday" },
];

export default function TransactionFormDialog({ open, onOpenChange, booking, onSaved }) {
  const [amount, setAmount] = useState("");
  const [initiative, setInitiative] = useState("regular");
  const [method, setMethod] = useState("cash");
  const [treatments, setTreatments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/treatments").then((r) => {
      setTreatments(r.data);
      if (booking?.treatment_id) {
        const t = r.data.find((x) => x.id === booking.treatment_id);
        if (t) setAmount(String(t.price));
      }
    });
    setInitiative("regular");
    setMethod("cash");
  }, [open, booking]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!amount || Number(amount) <= 0) {
      toast.error("Amount harus lebih dari 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/transactions", {
        patient_id: booking.patient_id,
        booking_id: booking.id,
        branch_id: booking.branch_id,
        doctor_id: booking.doctor_id || null,
        treatment_id: booking.treatment_id || null,
        initiative_type: initiative,
        amount: Number(amount),
        payment_method: method,
      });
      // also flip booking to finished
      await api.patch(`/bookings/${booking.id}/status`, { status: "finished" });
      toast.success("Transaksi tercatat & booking finished");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Catat Transaksi</DialogTitle>
          <DialogDescription>
            Pasien: <span className="font-medium text-foreground">{booking.patient_name}</span>
            <br />
            Treatment: {booking.treatment_name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Amount (Rp) *</Label>
            <Input
              type="number"
              min="0"
              step="1"
              data-testid="tx-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div>
            <Label>Tipe Inisiatif</Label>
            <Select value={initiative} onValueChange={setInitiative}>
              <SelectTrigger data-testid="tx-initiative-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INITIATIVE_OPTIONS.map((o) => (
                  <SelectItem key={o.v} value={o.v}>
                    {o.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Metode Pembayaran</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="tx-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Kartu</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="qris">QRIS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="tx-cancel-btn"
              onClick={() => onOpenChange(false)}
            >
              Batal
            </Button>
            <Button type="submit" data-testid="tx-save-btn" disabled={saving}>
              {saving ? "Menyimpan..." : "Catat & Finish Booking"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
